/* eslint-disable sonarjs/no-nested-template-literals */
import { isNonEmptyRecord, isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import type { INTEGRATION_TABS } from '~/components/integrations/IntegrationsScreen';
import type { PurchasePlanTypes } from './components/Plans/tieredPlans';
import type { ChannelPodcastSettingsRouteParams } from './screens/ChannelPodcastSettingsScreen/types';
import type { CustomizePageType } from './screens/MediaPlayerAndPageCustomizeScreen/domain/pages';

type SearchParamsInterface = Record<string, string> | string[][] | URLSearchParams | string | null;

const getQueryParams = (params?: SearchParamsInterface) => {
  return new URLSearchParams(params ?? {}).toString();
};

const pathWithParams = (path: string, params?: SearchParamsInterface) => {
  const queryString = isNonEmptyRecord(params) ? `?${getQueryParams(params)}` : '';
  return `${path}${queryString}`;
};

export const paths = {
  abTest: ({ hashedId }: { hashedId: string }): string => `/ab-tests/${hashedId}`,
  abTests: (): string => '/ab-tests',
  account: (): string => '/account',
  advancedSearch: (): string => '/search',
  advancedSearchUsers: (): string => '/search/users',
  allContent: (): string => '/content/folders',
  allMedia: (): string => '/content/media',
  //  delete and clean up the legacy media codebase and url
  allMediaV1: (): string => '/content/media-v1',
  analytics: (): string => '/account/analytics',
  anonymousEditor: (): string => '/anonymous/editor',
  anonymousRecord: (): string => '/anonymous/record',
  anonymousGroupRecord: ({ hashedId, joinKey }: { hashedId: string; joinKey: string }): string =>
    `/anonymous/group-record/${hashedId}/${joinKey}`,
  createGroupRecord: (): string => '/record/group/new',
  archived: (): string => '/content/archived',
  //  delete and clean up the legacy archived codebase and url
  archivedV1: (): string => '/content/archived-v1',
  archivedOverview: ({ mediaHashedId }: { mediaHashedId: string }): string =>
    `/archived/${mediaHashedId}`,
  audience: (): string => '/stats/audience',
  billingInfo: (): string => '/account/info',
  billingContacts: (): string => '/account/billing/contacts',
  billingInvoices: (): string => '/account/billing/invoices',
  paymentMethods: (): string => '/account/billing/payment-methods',
  billingOverview: (): string => '/account/billing',
  brandSettings: (): string => '/account/brand',
  integrations: (
    tab?: keyof typeof INTEGRATION_TABS,
    source?: string,
    integration?: string,
  ): string => {
    const params = {
      ...(isNotNil(tab) ? { tab } : {}),
      ...(isNotNil(source) ? { source } : {}),
      ...(isNotNil(integration) ? { integration } : {}),
    };

    return pathWithParams('/account/integrations', params);
  },
  channel: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}`,
  channelAnalytics: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/analytics`,
  channelDetails: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/details`,
  channelDistribution: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/podcast/distribution`,
  channelEditor: ({
    channelHashedId,
    mediaHashedId,
    includeUnpublished = false,
  }: {
    channelHashedId: string;
    mediaHashedId?: string;
    includeUnpublished?: boolean;
  }): string => {
    if (isNotNil(mediaHashedId) && includeUnpublished) {
      return `/channels/${channelHashedId}/customize/channel-embed?wchannelid=${channelHashedId}&wvideoid=${mediaHashedId}&unpublished_media_id_for_preview=${mediaHashedId}`;
    }

    if (isNotNil(mediaHashedId)) {
      return `/channels/${channelHashedId}/customize/channel-embed?wchannelid=${channelHashedId}&wvideoid=${mediaHashedId}`;
    }

    return `/channels/${channelHashedId}/customize/channel-embed`;
  },
  channelPlaylistEditor: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/customize/playlist-embed`,
  channelPodcastingChecklist: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/podcasting/checklist`,
  channelPodcastImport: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/podcast-import`,
  channelPromote: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/promote`,
  channels: (podcasting?: string): string => {
    const params = isNotNil(podcasting) ? { podcasting } : undefined;
    return pathWithParams('/content/channels', params);
  },
  channelPodcastSettings: ({
    channelHashedId,
    tab = 'checklist',
  }: ChannelPodcastSettingsRouteParams): string => `/channels/${channelHashedId}/podcasting/${tab}`,
  channelStats: (
    { channelHashedId }: { channelHashedId: string },
    searchParams: SearchParamsInterface = null,
  ): string => pathWithParams(`/channels/${channelHashedId}/stats`, searchParams),
  channelSubscribers: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/subscribers`,
  compareMedia: (searchParams = {}): string =>
    pathWithParams('/account/analytics/compare', searchParams),
  createContent: (): string => '/content/create',
  createChannel: ({
    podcasting,
    cancelRedirectTo,
    autoTrialPlan,
  }: {
    podcasting?: boolean;
    cancelRedirectTo?: string;
    autoTrialPlan?: string;
  } = {}): string => {
    const params = {
      type: 'channel',
      ...(isNotNil(podcasting) ? { podcasting: podcasting.toString() } : {}),
      ...(isNotNil(cancelRedirectTo) ? { cancelRedirectTo } : {}),
      ...(isNotNil(autoTrialPlan) ? { autoTrialPlan } : {}),
    };

    return pathWithParams('/content/create', params);
  },
  createLiveEvent: ({
    channelHashedId,
    preRecordedMediaHashedId,
  }: {
    channelHashedId?: string;
    preRecordedMediaHashedId?: string;
  } = {}): string => {
    if (isNonEmptyString(preRecordedMediaHashedId)) {
      return `/content/create?type=event&preRecordedMediaHashedId=${preRecordedMediaHashedId}`;
    }
    if (isNonEmptyString(channelHashedId)) {
      return `/content/create?type=event&channelHashedId=${channelHashedId}`;
    }
    return '/content/create?type=event';
  },
  createRecording: (
    source?:
      | 'checklist'
      | 'chrome-extension'
      | 'create-from-anywhere'
      | 'home'
      | 'integrations'
      | 'new-menu'
      | 'projects-fux'
      | 'record'
      | 'wtw-email'
      | 'wtw',
  ): string => {
    const url = '/record/new';
    return `${url}${isNotNil(source) ? `?source=${encodeURIComponent(source)}` : ''}`;
  },
  editMedia: ({
    mediaHashedId,
    searchParams,
  }: {
    mediaHashedId: string;
    searchParams?: SearchParamsInterface;
  }): string =>
    `/editor?hashedId=${mediaHashedId}${isNotNil(searchParams) ? `&${getQueryParams(searchParams)}` : ''}`,
  editor: (): string => '/editor',
  createSocialClips: (mediaHashedId: string): string => `/medias/${mediaHashedId}/social-clips`,
  rescript: (mediaHashedId: string): string => `/medias/${mediaHashedId}/rescript`,
  emailSettings: (): string => '/account/email-settings',
  deprecatedEpisodeDetails: ({ episodeHashedId }: { episodeHashedId: string }): string =>
    `/episodes/${episodeHashedId}/details`,
  episode: ({
    episodeHashedId,
    channelHashedId,
  }: {
    episodeHashedId: string;
    channelHashedId: string;
  }): string => `/channels/${channelHashedId}?channel_episode_id=${episodeHashedId}`,
  favorites: (): string => '/favorites',
  groupRecord: ({ hashedId }: { hashedId: string }): string => `/record/group/${hashedId}`,
  groupRecordGuestTrial: (): string => '/anonymous/group-record/guest-trial',
  home: (): string => '/home',
  leadCaptureAnalytics: (
    mediaHashedId: string,
    searchParams: SearchParamsInterface = null,
  ): string => pathWithParams(`/medias/${mediaHashedId}/analytics/conversion`, searchParams),
  trafficAnalytics: (mediaHashedId: string, searchParams: SearchParamsInterface = null): string =>
    pathWithParams(`/medias/${mediaHashedId}/analytics/traffic`, searchParams),
  liveEventRecapAnalytics: (
    mediaHashedId: string,
    searchParams: SearchParamsInterface = null,
  ): string => pathWithParams(`/medias/${mediaHashedId}/analytics/event_recap`, searchParams),
  liveStream: (liveStreamHashedId: string): string => `/live/${liveStreamHashedId}`,
  liveStreams: (): string => '/content/live',
  liveStreamRegistration: (liveStreamHashedId: string): string =>
    `/live/${liveStreamHashedId}/registration`,
  liveStreamCustomizeRegistrationPage: (liveStreamHashedId: string): string =>
    `/live/${liveStreamHashedId}/customize/registration`,
  contentLiveScheduled: (): string => '/content/live/scheduled',
  contentLiveCompleted: (): string => '/content/live/completed',
  folder: ({ hashedId }: { hashedId: string }): string => `/folders/${hashedId}`,
  subfolder: ({
    folderHashedId,
    subfolderHashedId,
  }: {
    folderHashedId: string;
    subfolderHashedId: string;
  }): string => `/folders/${folderHashedId}/subfolders/${subfolderHashedId}`,
  media: ({
    mediaHashedId,
    searchParams,
  }: {
    mediaHashedId: string;
    searchParams?: SearchParamsInterface;
  }): string => pathWithParams(`/medias/${mediaHashedId}`, searchParams),
  mediaAdmin: ({ mediaHashedId }: { mediaHashedId: string }): string =>
    `/medias/${mediaHashedId}/admin`,
  mediaAnalytics: (mediaHashedId: string, searchParams: SearchParamsInterface = null): string =>
    pathWithParams(`/medias/${mediaHashedId}/analytics`, searchParams),
  mediaCustomize: ({ hashedId }: { hashedId: string }): string => `/medias/${hashedId}/customize`,
  // TODO: Write up some types to enforce page and section combinations appropriately
  mediaPlayerAndPageCustomize: ({
    hashedId,
    page,
    section,
  }: {
    hashedId: string;
    page: CustomizePageType;
    section?: string;
  }): string =>
    `/medias/${hashedId}/joint-customize/${page}${isNotNil(section) ? `/${section}` : ''}`,
  mediaCustomizeTurnstile: ({ hashedId }: { hashedId: string }): string =>
    `/medias/${hashedId}/customize/turnstile`,
  mediaPreview: (mediaHashedId: string): string => `/medias/${mediaHashedId}/preview`,
  mediaRequest: (mediaRequestHashedId: string): string => `/request/${mediaRequestHashedId}`,
  mediaSocialStats: (mediaHashedId: string, searchParams: SearchParamsInterface = null): string =>
    pathWithParams(`/stats/medias/${mediaHashedId}#social`, searchParams),
  mediaStats: (mediaHashedId: string, searchParams: SearchParamsInterface = null): string =>
    pathWithParams(`/stats/medias/${mediaHashedId}`, searchParams),
  notifications: (): string => '/account/notifications',
  payment: ({
    planType,
    cycle = null,
    changing = false,
  }: {
    planType?: PurchasePlanTypes;
    cycle?: string | null;
    changing?: boolean;
  }): string => {
    const params: string[] = [];

    if (planType == null) {
      return '/account/plans';
    }
    if (cycle != null) {
      params.push(`cycle=${cycle}`);
    }

    if (changing) {
      params.push('changing=true');
    }

    return `/account/payment/${planType}${params.length > 0 ? `?${params.join('&')}` : ''}`;
  },
  plans: (): string => '/account/plans',
  podcastChecklist: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/podcast/checklist`,
  podcastEpisodeAnalytics: (
    mediaHashedId: string,
    searchParams: SearchParamsInterface = null,
  ): string => pathWithParams(`/medias/${mediaHashedId}/analytics/podcast`, searchParams),
  podcastFeedDetails: ({ channelHashedId }: { channelHashedId: string }): string =>
    `/channels/${channelHashedId}/podcast`,
  project: ({ projectId }: { projectId: string }): string => `/folders/${projectId}`,
  folderAnalytics: (
    { projectHashedId }: { projectHashedId: string },
    searchParams?: SearchParamsInterface,
  ): string => pathWithParams(`/folders/${projectHashedId}/analytics`, searchParams),
  projects: (): string => '/content/folders',
  folders: (): string => '/content/folders',
  projectStats: ({ projectHashedId }: { projectHashedId: string }): string =>
    `/stats/projects/${projectHashedId}`,
  transcriptDownload: ({
    mediaHashedId,
    format = 'txt',
    ietfLanguageTag,
    disposition,
  }: {
    mediaHashedId: string;
    format?: 'srt' | 'txt' | 'vtt';
    ietfLanguageTag?: string;
    disposition?: string;
  }): string => {
    const params: Record<string, string> = {};
    if (isNonEmptyString(ietfLanguageTag)) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      params['ietf_language_tag'] = ietfLanguageTag;
    }
    if (isNonEmptyString(disposition)) {
      params.disposition = disposition;
    }
    return pathWithParams(
      `/medias/${mediaHashedId}/time_coded_transcripts/download.${format}`,
      params,
    );
  },
  recordBeta: (): string => '/record/beta',
  requestVideo: (): string => '/account/video-requests',
  studio: (origin: string, tab: 'edit' | 'record' = 'edit', anonStatus?: string): string => {
    const params: string[] = [];
    params.push(`origin=${encodeURIComponent(origin)}`);
    params.push(`tab=${tab}`);

    if (isNotNil(anonStatus)) {
      params.push(`anonymousRecord=${anonStatus}`);
    }

    return `/studio${params.length > 0 ? `?${params.join('&')}` : ''}`;
  },
  tags: (): string => '/account/tags',
  trends: (): string => '/stats/account',
  usage: (): string => '/account/usage',
  users: (): string => '/users',
  manageUser: ({ hashedId }: { hashedId: string }): string => `/users#/user/${hashedId}`,
  manageGroup: ({ hashedId }: { hashedId: string }): string => `/users#/group/${hashedId}`,
  userProfile: (): string => '/user/profile',
  viewStream: (): string => '/stats/views',
  wtwWelcome: (): string => '/account/welcome',
  wtwCustomize: (): string => '/account/welcome/customize',
  wtwMobile: (): string => '/account/welcome/mobile',
  wtwTrials: (searchParams: SearchParamsInterface = null): string =>
    pathWithParams('/account/welcome/trials', searchParams),
  wtwAdd: (experimentKey?: string, variant?: string): string => {
    const path = '/account/welcome/add';
    return isNonEmptyString(variant) && isNonEmptyString(experimentKey)
      ? pathWithParams(path, { [experimentKey]: variant })
      : path;
  },
};
